import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";

const BG_IMG = "https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/admin", { replace: true });
  }, [user, nav]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="admin-login-page">
      <div className="relative hidden md:block">
        <img src={BG_IMG} alt="Studio" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="bg-white/95 p-3 inline-block self-start">
            <Logo className="h-10" testid="login-side-logo" />
          </div>
          <div>
            <div className="text-xs uppercase font-medium tracking-[0.22em] text-white/70 mb-3">Studio Manager</div>
            <div className="font-serif text-4xl font-light leading-tight max-w-md">
              Bookings, invoices, and reminders — all in one calm place.
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm">
          <Logo className="h-10 mb-8" testid="login-logo" />
          <div className="eyebrow mb-3">Studio Manager</div>
          <h1 className="font-serif text-4xl font-light leading-tight">Welcome back.</h1>
          <p className="mt-3 text-sm text-muted-foreground">Sign in to manage bookings, services, and invoices.</p>

          <div className="mt-10 space-y-6">
            <label className="block">
              <span className="eyebrow">Email</span>
              <input
                required
                type="email"
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-transparent border-b border-border py-3 outline-none focus:border-foreground"
              />
            </label>
            <label className="block">
              <span className="eyebrow">Password</span>
              <input
                required
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-transparent border-b border-border py-3 outline-none focus:border-foreground"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="mt-10 w-full inline-flex items-center justify-center px-7 h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <Link to="/" className="mt-6 block text-center text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground">
            ← Back to site
          </Link>
        </form>
      </div>
    </div>
  );
}
